import { BookingRelationshipsEntity, BookingsSearchParams, BookingsSearchQuery } from '@jurnee/common/src/entities/Booking';
import { AddressJSON, DEFAULT_UTC_TIMEZONE } from '../entities/Address';
import { ApprovalRequestJSON } from '../entities/ApprovalRequest';
import { ApprovalResponse } from '../entities/ApprovalResponse';
import BookingEntity, { BookingJSON, BookingRelationshipsJSON, BookingStatus } from '../entities/Booking';
import { BookingItem, BookingItemJSON } from '../entities/BookingItem';
import { BookingOrganizerJSON } from '../entities/BookingOrganizer';
import { BookingReviewJSON } from '../entities/BookingReview';
import { ChangeRequest } from '../entities/ChangeRequest';
import { Currency } from '../entities/Currency';
import ExternalCostEntity, { ExternalCost, ExternalCostJSON } from '../entities/ExternalCost';
import { Invoice, InvoiceJSON } from '../entities/Invoice';
import { OfficeJSON } from '../entities/Office';
import { PartnerJSON } from '../entities/Partner';
import { PartnerDocument } from '../entities/PartnerDocument';
import { PropositionJSON } from '../entities/Proposition';
import { PropositionsGroup, PropositionsGroupJSON } from '../entities/PropositionsGroup';
import { Quote, QuoteJSON } from '../entities/Quote';
import { Task } from '../entities/Task';
import UserEntity, { UserJSON } from '../entities/User';
import { getAddress, getTimezone } from './addresses';
import { hasAnApprovalRequestApproved, hasInitialApprovalResponses } from './approvalRequests';
import { sortByDate } from './arrays';
import { BillingPeriodStateInput, SettlementInvoice, getBookingBillingState } from './billingPeriods';
import { getBookingItemImagePath, getItemConvertedTotalPrice, hasConfirmedStatus, hasFinishedStatus, hasInitialStatus, hasPartner } from './bookingsItems';
import { getCdnImageUrl } from './core';
import { CurrencyConversion, getCurrencyByCountryCode } from './currencies';
import { convertToTimezone, getCurrentTimeZone, isDatePast } from './dates';
import { getDiscountPercent, getInvoicesDetails, getTaxesPercent, hasAnInvoicePaidByCard, hasCommitment, hasQuotePending, hasSetupIntent, haveAllASetupIntent } from './invoices';
import { roundAmount } from './prices';
import { hasAPropositionsGroupOpen, hasAPropositionsGroupWaitingPropososals } from './propositionsGroups';
import { areAllQuotesSigned } from './quotes';
import { isEmpty } from './strings';
import { getUserLabel } from './user';

type Relationships = {
  approvalRequests?: ApprovalRequestJSON[];
  invoices?: InvoiceJSON[];
  bookingsItems?: BookingItemJSON[]
  bookingsReviews?: BookingReviewJSON[];
  externalCosts?: ExternalCostJSON[];
  propositionsGroups?: PropositionsGroupJSON[];
  propositions?: PropositionJSON[];
  quotes?: QuoteJSON[];
}

export type RelationshipsIndex = {
  [K in keyof Required<Relationships>]: Map<number, NonNullable<Relationships[K]>>;
};

interface GetParticipantsParams {
  bookingsItems?: Pick<BookingItemJSON, 'participants'>[];
  propositionsGroups?: Pick<PropositionsGroupJSON, 'participants'>[];
};

interface BookingItemData extends Pick<BookingItemJSON, 'locationType' | 'address'> {
  partner: Pick<PartnerJSON, 'address'> | null;
  office: Pick<OfficeJSON, 'address'> | null;
  from: Date | string;
}

interface GetBookingFromParams {
  bookingsItems?: BookingItemData[];
  externalCosts?: { eventDate: Date | string }[];
  propositionsGroups?: (Pick<PropositionsGroupJSON, 'address' | 'office'> & { from: Date | string | null })[];
};

interface GetInternalStatusParams {
  commitmentType: string | null;
  booking: {
    canceledAt: Date | string | null;
    invoices?: SettlementInvoice[];
    billingPeriod?: BillingPeriodStateInput | null;
    bookingsItems: (Pick<BookingItem, 'status'> & { changeRequests: Pick<ChangeRequest, 'acceptedAt' | 'declinedAt' | 'canceledAt'>[] })[];
    partnersDocuments: Pick<PartnerDocument, 'status'>[];
    propositionsGroups: Pick<PropositionsGroup, 'status'>[];
    externalCosts: ExternalCost[];
    tasks: Pick<Task, 'status'>[];
  }
};

interface GetBookingCountryCodeParams {
  bookingsItems?: {
    locationType: BookingItemJSON['locationType'];
    address: Pick<AddressJSON, 'countryCode'> | null;
    partner: { address: Pick<AddressJSON, 'countryCode'> | null } | null;
    office: { address: Pick<AddressJSON, 'countryCode'> } | null;
  }[];
  propositionsGroups?: {
    address: Pick<AddressJSON, 'countryCode'> | null;
    office: { address: Pick<AddressJSON, 'countryCode'> } | null;
  }[];
};

interface GetCostDetailsParams {
  bookingsItems: BookingItemJSON[];
  invoices: InvoiceJSON[];
  currencyConversion: Omit<CurrencyConversion, 'source'>;
  platformFeePercent: number;
}

interface GetCommitmentDateParams {
  quotes: {
    status: QuoteJSON['status'];
    acceptedAt: Date | string | null
  }[];
  approvalRequests: {
    approvalResponses: {
      approvedAt: Date | string | null;
      status: ApprovalResponse['status']
    }[]
  }[];
  invoices: {
    providerSetupIntentId: string | null;
    status: InvoiceJSON['status'];
    finalizedAt: Date | string | null
  }[];
}

export const DEFAULT_BOOKING_DEADLINE = 7;
export const DEFAULT_BOOKING_DURATION = 60;
export const PLACEHOLDER_PATH = 'images/booking_placeholder.jpeg';
const SUPPORTED_FILTER_STATUSES: BookingStatus[] = ['CANCELED'];

export function getBookingFrom(relationships: GetBookingFromParams): { date: Date | null; timezone: string } {
  const { bookingsItems = [], externalCosts = [], propositionsGroups = [] } = relationships;

  let out: { date: Date | null; timezone: string } = { date: null, timezone: DEFAULT_UTC_TIMEZONE };

  for (const item of bookingsItems) {
    const date = new Date(item.from);
    const timezone = getTimezone(item);

    if (!out.date || date < out.date) {
      out = { date, timezone };
    }
  }

  for (const { eventDate } of externalCosts) {
    const date = new Date(eventDate);

    if (!out.date || date < out.date) {
      out = { date, timezone: DEFAULT_UTC_TIMEZONE };
    }
  }

  if (out.date) {
    return out;
  }

  for (const propositionsGroup of propositionsGroups) {
    if (propositionsGroup.from) {
      const date = new Date(propositionsGroup.from);
      const timezone = getTimezone(propositionsGroup);

      if (!out.date || date < out.date) {
        out = { date, timezone };
      }
    }
  }

  return out;
}

export function getBookingParticipants({ bookingsItems = [], propositionsGroups = [] }: GetParticipantsParams): number {
  if (bookingsItems.length > 0) {
    return bookingsItems.reduce(
      (max, { participants }) => Math.max(max, participants),
      0
    );
  }

  return propositionsGroups.reduce(
    (max, group) => Math.max(max, group.participants || 0),
    0
  );
}

export function isBookingInitial({ bookingsItems }: BookingJSON | BookingEntity): boolean {
  if (bookingsItems.length === 0) {
    return false;
  }

  return bookingsItems.every(hasInitialStatus);
}

export function hasPartners({ bookingsItems }: BookingEntity): boolean {
  if (bookingsItems.length === 0) {
    return false;
  }

  return bookingsItems.every(hasPartner);
}

export function hasMissingTimezone({ bookingsItems }: BookingEntity): boolean {
  if (bookingsItems.length === 0) {
    return true;
  }

  return bookingsItems.some((item) => getTimezone(item) === DEFAULT_UTC_TIMEZONE);
}

export function isBookingConfirmed({ bookingsItems }: BookingJSON | BookingEntity): boolean {
  if (bookingsItems.length === 0) {
    return false;
  }

  return bookingsItems.some(hasConfirmedStatus) && bookingsItems.every((bookingsItem) => hasConfirmedStatus(bookingsItem) || hasFinishedStatus(bookingsItem));
}

export function isBookingFinished({ bookingsItems }: BookingJSON | BookingEntity, externalCosts: (ExternalCostEntity | ExternalCostJSON)[]): boolean {
  if (bookingsItems.length === 0 && externalCosts.length === 0) {
    return false;
  }

  return bookingsItems.every(hasFinishedStatus) && externalCosts.every(({ eventDate }) => new Date(eventDate) < new Date());
}

export function isBookingCanceled({ canceledAt }: BookingJSON | BookingEntity): boolean {
  return !!canceledAt;
}

export function isBookingPassed({ bookingsItems }: BookingJSON): boolean {
  if (bookingsItems.length === 0) {
    return false;
  }

  return bookingsItems.every(({ to }) => isDatePast(new Date(to)));
}

export function getMainBookingRelation(booking: BookingJSON): BookingJSON['bookingsItems'][0] | null {
  return booking.bookingsItems.length > 0 ? sortByDate(booking.bookingsItems, 'from')[0] : null;
}

export function getBookingItemImageUrl(bookingItem: Pick<BookingItemJSON, 'bookingsItemsImages'> | null) {
  if (!bookingItem) {
    return getCdnImageUrl(PLACEHOLDER_PATH);
  }

  const path = getBookingItemImagePath(bookingItem);

  return getCdnImageUrl(path);
}

export function getBookingImageUrl(booking: BookingJSON) {
  const bookingItem = getMainBookingRelation(booking);

  return getBookingItemImageUrl(bookingItem);
}

export function getBookingName(booking: Pick<BookingJSON, 'name'> & { createdAt: string | Date }) {
  if (!booking.name || isEmpty(booking.name)) {
    const date = (new Date(booking.createdAt)).toISOString().slice(0, 10);

    return `Booking ${date}`;
  }

  return booking.name.trim();
}

export function getMainOrganizerId(bookingOrganizers: BookingJSON['bookingsOrganizers']) {
  const sortedBookingOrganizers = [...bookingOrganizers].sort((a,b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  return sortedBookingOrganizers.length > 0 ? sortedBookingOrganizers[0].organizerId : null;
}

export function getOrganizerLabel(bookingOrganizers: BookingOrganizerJSON[], users: UserJSON[]) {
  const organizerId = getMainOrganizerId(bookingOrganizers);
  const organizer = users.find(user => user.id === organizerId);

  return organizer ? getUserLabel(organizer) : '-';
}

export function isBookingCommitted({ approvalRequests = [], invoices = [], quotes = [] }: Pick<(BookingRelationshipsJSON), 'approvalRequests' | 'invoices' | 'quotes'>) {
  if (invoices.length === 0) {
    return false;
  }

  return invoices.every(invoice => {
    const currentQuotes = quotes.filter(({ providerInvoiceId }) => providerInvoiceId === invoice.providerInvoiceId);
    const currentApprovalRequests = approvalRequests.filter(({ providerInvoiceId }) => providerInvoiceId === invoice.providerInvoiceId);

    if (hasSetupIntent(invoice)) {
      return true;
    }

    return hasCommitment(invoice, currentQuotes, currentApprovalRequests);
  });
}

export function hasPendingCommitment({ approvalRequests = [], quotes = [] }: Pick<(BookingRelationshipsJSON), 'approvalRequests' | 'invoices' | 'quotes'>) {
  const hasPendingApprovalRequest = approvalRequests.some(hasInitialApprovalResponses);
  const hasPendingQuote = hasQuotePending(quotes);

  return hasPendingApprovalRequest || hasPendingQuote;
}

export function getBookingStatus(booking: BookingJSON | BookingEntity, relationships: Pick<(BookingRelationshipsJSON | BookingRelationshipsEntity), 'approvalRequests' | 'invoices' | 'externalCosts' | 'propositionsGroups' | 'propositions' | 'quotes'>): BookingStatus {
  const { approvalRequests, invoices, externalCosts, propositionsGroups, propositions, quotes } = relationships;

  if (isBookingCanceled(booking)) {
    return 'CANCELED';
  }

  if (isBookingFinished(booking, externalCosts)) {
    return 'FINISHED';
  }

  if (isBookingConfirmed(booking)) {
    return 'CONFIRMED';
  }

  if (hasAnApprovalRequestApproved(approvalRequests)) {
    return 'CONFIRMING_AVAILABILITY';
  }

  if (areAllQuotesSigned(quotes)) {
    return 'CONFIRMING_AVAILABILITY';
  }

  if (haveAllASetupIntent(invoices)) {
    return 'CONFIRMING_AVAILABILITY';
  }

  if (approvalRequests.some(hasInitialApprovalResponses)) {
    return 'AWAITING_APPROVAL';
  }

  if (hasQuotePending(quotes)) {
    return 'AWAITING_QUOTE';
  }

  if (hasAPropositionsGroupOpen(propositionsGroups)) {
    return 'PROPOSITION_READY';
  }

  if (hasAPropositionsGroupWaitingPropososals(propositionsGroups, propositions)) {
    return 'PROCESSING';
  }

  if (booking.bookingsItems.length === 0 && externalCosts.length > 0) {
    return 'EXTERNAL_COSTS';
  }

  if (booking.bookingsItems.length > 0 && booking.bookingsItems.every(hasInitialStatus)) {
    return 'WAITING_COMMITMENT';
  }

  return 'DRAFT';
}

export function getCommitmentType({ quotes, approvalRequests, invoices }: GetCommitmentDateParams) {
  if (areAllQuotesSigned(quotes)) {
    return 'QUOTE';
  }

  if (hasAnApprovalRequestApproved(approvalRequests)) {
    return 'APPROVAL_REQUEST';
  }

  if (hasAnInvoicePaidByCard(invoices)) {
    return 'CREDIT_CARD';
  }

  return null;
}

export function getCommitmentDate(booking: GetCommitmentDateParams) {
  switch (getCommitmentType(booking)) {
  case 'QUOTE': {
    const quotes = booking.quotes as (Quote & { acceptedAt: Date })[];
    return sortByDate(quotes, 'acceptedAt', 'desc')[0].acceptedAt;
  }
  case 'CREDIT_CARD': {
    const invoices = booking.invoices as (Invoice & { finalizedAt: Date })[];
    return sortByDate(invoices, 'finalizedAt', 'desc')[0].finalizedAt;
  }
  case 'APPROVAL_REQUEST': {
    const approvalResponses = booking.approvalRequests.flatMap(({ approvalResponses }) => approvalResponses) as (ApprovalResponse & { approvedAt: Date })[];
    return sortByDate(approvalResponses, 'approvedAt', 'desc')[0].approvedAt;
  }
  default:
    return null;
  }
}

function getFilterStatusArray(status: string | string[]): BookingStatus[] {
  const statusArray = Array.isArray(status) ? status : [status];

  return statusArray
    .map(status => status.toUpperCase() as BookingStatus)
    .filter(status => SUPPORTED_FILTER_STATUSES.includes(status));
}

export function buildBookingsSearchParams(query: BookingsSearchQuery): BookingsSearchParams {
  const params: BookingsSearchParams = {};

  if (query.before) {
    params.before = Number(query.before);
  }

  if (query.after) {
    params.after = Number(query.after);
  }

  if (query.status) {
    const statusArray = getFilterStatusArray(query.status);

    if (statusArray.length > 0) {
      params.status = statusArray;
    }
  }

  return params;
}

export function sortByFrom<T extends BookingJSON, R extends Relationships>(list: T[], relationships?: R): T[] {
  const index = createRelationshipsIndex(list, relationships);

  return [...list].sort((a, b) => {
    const { date: dateA } = getBookingFrom(getFilteredRelationshipsFromIndex(a.id, index));
    const { date: dateB } = getBookingFrom(getFilteredRelationshipsFromIndex(b.id, index));

    if (dateA && !dateB) {
      return -1;
    }

    if (!dateA && dateB) {
      return 1;
    }

    return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
  });
}

function groupByTime<T extends BookingJSON, R extends BookingRelationshipsJSON>(
  list: T[],
  getDate: (date: Date) => Date,
  relationships: R
): { [key: number]: T[] } {
  const index = createRelationshipsIndex(list, relationships);

  return [...list].reduce((map, booking) => {
    const bookingRelationships = getFilteredRelationshipsFromIndex(booking.id, index);
    const from = getBookingFrom({ ...bookingRelationships, bookingsItems: booking.bookingsItems });

    if (!from.date) {
      return map;
    }

    const dateInTimezone = convertToTimezone(from.date, from.timezone, getCurrentTimeZone());
    const time = getDate(dateInTimezone).getTime();

    if (Array.isArray(map[time])) {
      map[time].push(booking);
    } else {
      map[time] = [booking];
    }

    return map;
  }, {} as Record<number, T[]>);
}

export function groupByDay<T extends BookingJSON, R extends BookingRelationshipsJSON>(list: T[], relationships: R): { [key: number]: T[] } {
  return groupByTime(
    list,
    (from) => new Date(from.getFullYear(), from.getMonth(), from.getDate()),
    relationships
  );
}

export function groupByMonth<T extends BookingJSON, R extends BookingRelationshipsJSON>(
  list: T[],
  relationships: R
): { [key: number]: T[] } {
  return groupByTime(
    list,
    (from) => new Date(from.getFullYear(), from.getMonth()),
    relationships
  );
}

export function isBookingOrganizer({ bookingsOrganizers }: Pick<BookingEntity, 'bookingsOrganizers'>, userId: UserEntity['id']) {
  return bookingsOrganizers.some(({ organizerId }) => organizerId === userId);
}

export function filterRegisteredBookingsByUser(bookings: BookingEntity[], { email }: UserEntity) {
  return bookings.filter(({ budgetId, bookingsParticipants }) => {
    const isUserRegistered = bookingsParticipants.some(bookingParticipant =>
      bookingParticipant.participant.email === email && bookingParticipant.useBudget
    );

    return !budgetId && isUserRegistered;
  });
}

export function getOrganizerIds(booking: Pick<BookingEntity, 'bookingsOrganizers'>): number[] {
  return booking.bookingsOrganizers.map(bookingOrganizer => bookingOrganizer.organizerId);
}

export function createRelationshipsIndex<T extends Relationships>(bookings: BookingJSON[], relationships?: T): RelationshipsIndex {
  const index: RelationshipsIndex = {
    approvalRequests: new Map(),
    invoices: new Map(),
    bookingsItems: new Map(),
    bookingsReviews: new Map(),
    externalCosts: new Map(),
    propositions: new Map(),
    propositionsGroups: new Map(),
    quotes: new Map()
  };

  bookings.forEach(booking => {
    if (booking.bookingsItems.length > 0) {
      index.bookingsItems.set(booking.id, booking.bookingsItems);
    }
  });

  if (!relationships) {
    return index;
  }

  const bookingRelationships = Object.entries(relationships) as Array<[keyof Relationships, Relationships[keyof Relationships]]>;

  for (const [key, items] of bookingRelationships) {
    if (!Array.isArray(items) || ['propositions', 'bookingsItems'].includes(key)) {
      continue;
    }

    const relationshipMap = index[key] as Map<number, typeof items>;

    for (const item of items) {
      if (!('bookingId' in item)) {
        continue;
      }

      if (!item.bookingId) {
        continue;
      }

      const existing = relationshipMap.get(item.bookingId) || [];
      (existing as Array<typeof item>).push(item);
      relationshipMap.set(item.bookingId, existing);
    }
  };

  if (!Array.isArray(relationships.propositions) || !Array.isArray(relationships.propositionsGroups)) {
    return index;
  }

  for (const { id, bookingId } of relationships.propositionsGroups) {
    const propositions = relationships.propositions.filter(
      ({ propositionsGroupId }) => propositionsGroupId === id
    );

    if (propositions.length > 0) {
      const existing = index.propositions.get(bookingId) || [];
      index.propositions.set(bookingId, [...existing, ...propositions]);
    }
  }

  return index;
}

export function getFilteredRelationshipsFromIndex(bookingId: BookingJSON['id'], index: RelationshipsIndex) {
  return {
    approvalRequests: index.approvalRequests.get(bookingId) || [],
    invoices: index.invoices.get(bookingId) || [],
    bookingsItems: index.bookingsItems.get(bookingId) || [],
    bookingsReviews: index.bookingsReviews.get(bookingId) || [],
    externalCosts: index.externalCosts.get(bookingId) || [],
    propositions: index.propositions.get(bookingId) || [],
    propositionsGroups: index.propositionsGroups.get(bookingId) || [],
    quotes: index.quotes.get(bookingId) || []
  };
}

export function getInternalStatus({ booking, commitmentType }: GetInternalStatusParams) {
  if (booking.canceledAt) {
    return 'CANCELLED';
  }

  if (booking.bookingsItems.length === 0 && booking.propositionsGroups.length === 0 && booking.externalCosts.length > 0) {
    return 'EXTERNAL_COSTS';
  }

  if (booking.bookingsItems.length === 0 && booking.propositionsGroups.length === 0) {
    return 'DRAFT';
  }

  const hasDraftRequests = booking.propositionsGroups.some(group => group.status === 'DRAFT');

  if (hasDraftRequests) {
    return 'NEW';
  }

  if (booking.propositionsGroups.some(({ status }) => status === 'OPEN')) {
    return 'PROPOSITION_SENT';
  }

  const hasInitialItemsAndNoCommitment = booking.bookingsItems.length > 0 && booking.bookingsItems.every(hasInitialStatus) && commitmentType === null;

  if (hasInitialItemsAndNoCommitment) {
    return 'WAITING_COMMITMENT';
  }

  const isConfirmed = booking.bookingsItems.every(({ status }) => status === 'CONFIRMED');
  const hasPendingTasks = booking.tasks.some(({ status }) => ['TODO', 'IN_PROGRESS'].includes(status));
  const hasPendingChangeRequests = booking.bookingsItems.some(({ changeRequests }) => changeRequests.some(({ acceptedAt, declinedAt, canceledAt }) => !acceptedAt && !declinedAt && !canceledAt));
  const hasNotPaidDocuments = !booking.partnersDocuments.length || booking.partnersDocuments.some(({ status }) => status !== 'PAID');

  if (isConfirmed) {
    return hasPendingTasks || hasPendingChangeRequests || hasNotPaidDocuments ? 'CONFIRMED_TO_FINALIZE' : 'WAITING_EVENT';
  }

  const requiresInvoicing = booking.bookingsItems.some(({ status }) => status === 'FINISHED') &&
    getBookingBillingState(booking.invoices ?? [], booking.billingPeriod) !== 'PAID';

  if (requiresInvoicing) {
    return 'INVOICING';
  }

  if (booking.bookingsItems.every(({ status }) => status === 'FINISHED')) {
    return 'FINISHED';
  }

  if (commitmentType !== null) {
    return 'AVAILABILITY_TO_CONFIRM';
  }

  return 'UNKNOWN';
}

export function getBookingCountryCode({ bookingsItems = [], propositionsGroups = [] }: GetBookingCountryCodeParams): string | null{
  const bookingItem = bookingsItems[0];
  const bookingItemAddress = bookingItem ? getAddress(bookingItem) : null;

  const propositionGroup = propositionsGroups[0];
  const propositionGroupAddress = propositionGroup ? getAddress(propositionGroup) : null;

  const address = bookingItemAddress || propositionGroupAddress;

  return address ? address.countryCode : null;
}

export function getBookingCurrency({ bookingsItems = [], propositionsGroups = [] }: GetBookingCountryCodeParams): Currency {
  const countryCode = getBookingCountryCode({ bookingsItems, propositionsGroups });

  if (!countryCode) {
    return 'EUR';
  }

  return getCurrencyByCountryCode(countryCode) || 'EUR';
}

export function getCostDetails({
  bookingsItems,
  invoices,
  currencyConversion,
  platformFeePercent
}: GetCostDetailsParams) {
  const invoicesDetails = getInvoicesDetails(invoices, currencyConversion);

  const subtotalExclPlatformFee = roundAmount(getSubtotalExclPlatformFee(bookingsItems, currencyConversion));
  const platformFeeAmount = platformFeePercent > 0 ? roundAmount(invoicesDetails.subtotal - subtotalExclPlatformFee) : 0;
  const discountPercent = getDiscountPercent(invoicesDetails);
  const taxesPercent = getTaxesPercent(invoicesDetails);

  return {
    subtotalExclPlatformFee,
    platformFeeAmount,
    platformFeePercent,
    discountAmount: invoicesDetails.totalDiscount,
    discountPercent,
    taxesAmount: invoicesDetails.totalTax,
    taxesPercent,
    totalAmount: invoicesDetails.total
  };
}

export function getSubtotalExclPlatformFee(items: Pick<BookingItemJSON, 'unitAmountInclCommissionFee' | 'quantity' | 'currency'>[], currencyConversion: Omit<CurrencyConversion, 'source'>): number {
  return items.reduce((total, item) => {
    return total + getItemConvertedTotalPrice(item, currencyConversion);
  }, 0);
}